/** Structural edits of an outline as draft actions (the tour plan §5; the catalog
 *  itinerary's days): add, remove, restore, move within the parent and move under another
 *  parent. Each is ONE `SET` — one undo step — over the membership lists and the rows' own
 *  entries, with the caller's label (what the undo button and the history read: "Move
 *  Golden Circle to Day 4" — the record kind names its rows).
 *
 *  - add: the parent's list gains a new negative id (`nextTempId` over the kind's
 *    membership lists; the SET's `issued` — never issued again) at the end or at `at`; the
 *    row reads its level's defaults until
 *    edited, and `seed` values become its first entries;
 *  - remove: off its parent's list, its entries — and its descendants' — reverted with it
 *    (a new row vanishes; a saved one is deleted on save, and shown struck until then);
 *  - restore: a saved row back under its parent, at its stored position (or `at`); its own
 *    children the draft placed elsewhere meanwhile stay there (a row has one parent);
 *  - move: a row's place among its siblings, or a row under another parent (at its end).
 *    A SAVED row moves only under a saved parent — the server keeps its id (and what hangs
 *    off it) only between saved rows — otherwise the move is refused with the caller's
 *    reason.
 *
 *  Pure: they read the draft state and the baselines, and return the action. */
import { assertKindMemberships, nextTempId, type MembershipPaths } from '@/features/records/collections/membership';
import {
  parseRecordKey,
  recordKey,
  type DraftAction,
  type DraftChange,
  type DraftState,
  type RecordKey,
} from '@/features/records/draft/changeSet';

import { listIn, outlineDefault, outlinePlacement, outlineTree, type Baselines, type OutlinePlace, type OutlineSpec } from './tree';

export interface OutlineOpsContext {
  state: DraftState;
  baselines: Baselines;
  /** The record holding the top level's list. */
  ownerRec: RecordKey;
  spec: OutlineSpec;
  /** The record kind's ONE membership set (`membershipPaths`) — the same frozen instance
   *  its collection specs pass, never a subset: a new row's id is below every negative id
   *  any of its lists holds, and a new row with only default values is found only in its
   *  parent's list. A different instance for the same kind fails in development
   *  (`assertKindMemberships`). */
  memberships: MembershipPaths;
}

export type OutlineSetAction = Extract<DraftAction, { type: 'SET' }>;
export type OutlineOpResult = { action: OutlineSetAction; id?: number } | { refused: string };

const list = (ctx: OutlineOpsContext, rec: RecordKey, path: string) => listIn(ctx.spec, ctx.state.entries, ctx.baselines, rec, path);

function listChange(ctx: OutlineOpsContext, rec: RecordKey, path: string, to: number[]): DraftChange {
  const stored = ctx.baselines[rec]?.[path];
  return { rec, path, from: stored === undefined ? outlineDefault(ctx.spec, rec, path) : stored, to };
}

/** Every entry of `rec` back to what is stored (a stale one agrees with the server). */
function revertRecord(ctx: OutlineOpsContext, rec: RecordKey): DraftChange[] {
  return Object.entries(ctx.state.entries[rec] ?? {}).map(([path, entry]) => ({
    rec,
    path,
    from: entry.from,
    to: entry.stale ? entry.stale.current : entry.from,
  }));
}

/** A row's entries and its descendants' (children first), back to what is stored. */
function revertSubtree(ctx: OutlineOpsContext, level: number, id: number): DraftChange[] {
  const l = ctx.spec[level]!;
  const rec = recordKey(l.entity, id);
  const child = ctx.spec[level + 1];
  const below = child ? list(ctx, rec, child.list).flatMap((c) => revertSubtree(ctx, level + 1, c)) : [];
  return [...below, ...revertRecord(ctx, rec)];
}

const set = (changes: DraftChange[], label: string): OutlineSetAction => ({ type: 'SET', changes, label });

/** Where a row sits in the draft's outline (undefined: nowhere — removed, or unknown). */
export function placeOf(ctx: OutlineOpsContext, rec: RecordKey): OutlinePlace | undefined {
  return outlinePlacement(ctx.spec, outlineTree(ctx.spec, ctx.state.entries, ctx.baselines, ctx.ownerRec), ctx.ownerRec).get(rec);
}

/** Add a row on `level` under `parentRec` (the owning record for the top level) — at the
 *  end, or at index `at` of the parent's list. */
export function addRow(
  ctx: OutlineOpsContext,
  level: number,
  parentRec: RecordKey,
  { label, at, seed = {} }: { label: string; at?: number; seed?: Readonly<Record<string, unknown>> },
): { id: number; action: OutlineSetAction } {
  const l = ctx.spec[level]!;
  assertKindMemberships(ctx.ownerRec, ctx.memberships);
  const id = nextTempId(ctx.state, ctx.memberships);
  const next = list(ctx, parentRec, l.list);
  next.splice(at === undefined ? next.length : Math.max(0, Math.min(at, next.length)), 0, id);
  const rec = recordKey(l.entity, id);
  const changes = [listChange(ctx, parentRec, l.list, next)];
  for (const [path, to] of Object.entries(seed)) changes.push({ rec, path, from: l.defaults[path], to });
  return { id, action: { ...set(changes, label), issued: [id] } };
}

/** Remove a row: off its parent's list, its entries and its descendants' reverted. Null
 *  when the draft's outline does not hold it. */
export function removeRow(ctx: OutlineOpsContext, rec: RecordKey, label: string): OutlineOpResult | null {
  const at = placeOf(ctx, rec);
  if (!at) return null;
  const { id } = parseRecordKey(rec);
  const path = ctx.spec[at.level]!.list;
  return {
    action: set([listChange(ctx, at.parent, path, list(ctx, at.parent, path).filter((x) => x !== id)), ...revertSubtree(ctx, at.level, id)], label),
  };
}

/** Move a row to `toIndex` among its siblings. Null when it cannot move there. */
export function moveRow(ctx: OutlineOpsContext, rec: RecordKey, toIndex: number, label: string): OutlineOpResult | null {
  const at = placeOf(ctx, rec);
  if (!at) return null;
  const { id } = parseRecordKey(rec);
  const path = ctx.spec[at.level]!.list;
  const items = list(ctx, at.parent, path);
  const from = items.indexOf(id);
  if (from < 0 || toIndex < 0 || toIndex >= items.length || toIndex === from) return null;
  const next = items.filter((x) => x !== id);
  next.splice(toIndex, 0, id);
  return { action: set([listChange(ctx, at.parent, path, next)], label) };
}

/** Move a row under another parent (at the end of its list). Null when there is nothing
 *  to move; `refused` (the record kind's reason) when a saved row would go under a row
 *  the draft creates. */
export function moveRowTo(
  ctx: OutlineOpsContext,
  rec: RecordKey,
  toParentRec: RecordKey,
  { label, refused }: { label: string; refused: string },
): OutlineOpResult | null {
  const at = placeOf(ctx, rec);
  if (!at || at.parent === toParentRec) return null;
  const { id } = parseRecordKey(rec);
  if (id > 0 && parseRecordKey(toParentRec).id < 0) return { refused };
  const path = ctx.spec[at.level]!.list;
  return {
    action: set(
      [
        listChange(ctx, at.parent, path, list(ctx, at.parent, path).filter((x) => x !== id)),
        listChange(ctx, toParentRec, path, [...list(ctx, toParentRec, path), id]),
      ],
      label,
    ),
  };
}

/** A saved row back under `parentRec` on `level`: at its stored position, or at `at`.
 *  Its children the draft placed elsewhere meanwhile stay there. */
export function restoreRow(
  ctx: OutlineOpsContext,
  level: number,
  parentRec: RecordKey,
  id: number,
  { label, at }: { label: string; at?: number },
): OutlineOpResult {
  const l = ctx.spec[level]!;
  const items = list(ctx, parentRec, l.list);
  const stored = ctx.baselines[parentRec]?.[l.list];
  const pos = at ?? (Array.isArray(stored) ? (stored as number[]).indexOf(id) : -1);
  const next = [...items];
  next.splice(pos < 0 ? next.length : Math.min(pos, next.length), 0, id);
  const changes = [listChange(ctx, parentRec, l.list, next)];
  const child = ctx.spec[level + 1];
  if (child) {
    const rec = recordKey(l.entity, id);
    const placed = outlinePlacement(ctx.spec, outlineTree(ctx.spec, ctx.state.entries, ctx.baselines, ctx.ownerRec), ctx.ownerRec);
    const own = list(ctx, rec, child.list);
    const kept = own.filter((c) => !placed.has(recordKey(child.entity, c)));
    if (kept.length !== own.length) changes.push(listChange(ctx, rec, child.list, kept));
  }
  return { action: set(changes, label) };
}
